import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { materialService } from "../../services/materialService";
import { binService } from "../../services/binService";
import { useAuth } from "../../contexts/AuthContext";
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { anonymousBinService } from "../../utils/anonymousBinService";
import ValidatedBinChangeModal from "../Common/ValidatedBinChangeModal";

const MaterialDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [material, setMaterial] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [addingToBin, setAddingToBin] = useState(false);
  const [message, setMessage] = useState("");
  const [validatedWarningOpen, setValidatedWarningOpen] = useState(false);
  const [confirmingAdd, setConfirmingAdd] = useState(false);
  const [sampleModalOpen, setSampleModalOpen] = useState(false);
  const [activeSampleUrl, setActiveSampleUrl] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    const fetchMaterial = async () => {
      try {
        const data = await materialService.getMaterialById(id);
        setMaterial(data);
      } catch (error) {
        console.error("Error fetching material:", error);
        setMessage("Error loading material details");
      } finally {
        setLoading(false);
      }
    };

    fetchMaterial();
  }, [id]);

  const runAddToBin = async () => {
    if (!user) {
      anonymousBinService.addToBin({
        material,
        quantity,
      });
    } else {
      await binService.addToBin({
        materialId: material._id,
        quantity: quantity,
      });
    }
    if (typeof window !== "undefined" && window.refreshNavbarCounts) {
      window.refreshNavbarCounts();
    }
    setMessage("Material added to bin successfully!");
    setTimeout(() => {
      navigate("/bin");
    }, 1500);
  };

  const handleAddToBin = async () => {
    if (quantity <= 0) {
      setMessage("Quantity must be greater than 0");
      return;
    }

    setAddingToBin(true);
    setMessage("");

    try {
      if (!user) {
        await runAddToBin();
        return;
      }

      const res = await binService.getUserBin();
      const raw = res?.data?.bin ?? res?.bin;
      if (raw?.validationStatus) {
        setValidatedWarningOpen(true);
        return;
      }

      await runAddToBin();
    } catch (error) {
      console.error("Error adding to bin:", error);
      setMessage(
        error.response?.data?.message || "Error adding material to bin"
      );
    } finally {
      setAddingToBin(false);
    }
  };

  const handleConfirmValidatedAdd = async () => {
    setConfirmingAdd(true);
    try {
      await runAddToBin();
      setValidatedWarningOpen(false);
    } catch (error) {
      console.error("Error adding to bin:", error);
      setMessage(
        error.response?.data?.message || "Error adding material to bin"
      );
    } finally {
      setConfirmingAdd(false);
    }
  };

  const handleQuantityChange = (e) => {
    const value = parseFloat(e.target.value);
    if (value >= 0) {
      setQuantity(value);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!material) {
    return (
      <div className="bg-white rounded-lg shadow p-12 text-center">
        <p className="text-gray-600 text-lg">Material not found</p>
        <button
          onClick={() => navigate("/materials")}
          className="mt-4 text-green-600 hover:text-green-700 font-medium"
        >
          Back to Materials
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ValidatedBinChangeModal
        open={validatedWarningOpen}
        onCancel={() => {
          if (confirmingAdd) return;
          setValidatedWarningOpen(false);
        }}
        onConfirm={handleConfirmValidatedAdd}
        confirming={confirmingAdd}
      />
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <button
          onClick={() => navigate("/materials")}
          className="flex items-center text-green-600 hover:text-green-700 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Materials
        </button>
        <h1 className="text-3xl font-bold text-gray-900">{material.name}</h1>
        <p className="text-gray-600 mt-2">
          Category:{" "}
          {material.category
            .replace("_", " ")
            .replace(/\b\w/g, (l) => l.toUpperCase())}
        </p>
      </div>

      {/* Material Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Image */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Material Image
          </h2>
          {material.image ? (
            <img
              src={material.image}
              alt={material.name}
              className="w-full h-96 object-cover rounded-lg"
            />
          ) : (
            <div className="w-full h-96 bg-gray-200 rounded-lg flex items-center justify-center">
              <span className="text-gray-500 text-lg">No Image Available</span>
            </div>
          )}
        </div>

        {/* Details */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Material Details
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Price per kg
              </label>
              <p className="text-3xl font-bold text-green-600">
                ₦{material.pricePerKg?.toFixed(2) || "0.00"}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Category
              </label>
              <p className="text-gray-900">
                {material.category
                  .replace("_", " ")
                  .replace(/\b\w/g, (l) => l.toUpperCase())}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <p className="text-gray-900">
                {material.description || "No description available"}
              </p>
            </div>

            {material.exemptions && material.exemptions.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Instructions
                </label>
                <ul className="list-disc list-inside space-y-1">
                  {material.exemptions.map((exemption, index) => (
                    <li key={index} className="text-gray-900 text-sm">
                      {exemption}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {!material.exemptions &&
              material.excemptions &&
              material.excemptions.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Instructions
                  </label>
                  <ul className="list-disc list-inside space-y-1">
                    {material.excemptions.map((exemption, index) => (
                      <li key={index} className="text-gray-900 text-sm">
                        {exemption}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

            {/* Quantity Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quantity (kg)
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={quantity}
                  onChange={handleQuantityChange}
                  className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-2 rounded-md"
                >
                  +
                </button>
                <button
                  onClick={() => setQuantity(Math.max(0, quantity - 1))}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-2 rounded-md"
                >
                  -
                </button>
              </div>
            <div className="mt-3 flex items-start space-x-2 rounded-md border border-dashed border-green-300 bg-green-50/70 px-3 py-2 text-sm text-green-700">
              <InformationCircleIcon className="h-5 w-5 flex-shrink-0 text-green-500" />
              <span>
                Estimate the quantity in kilograms. A Jhonks agent will weigh it accurately when we collect your materials.
              </span>
            </div>
            </div>

            {/* Total Price */}
            <div className="border-t pt-4">
              <div className="flex justify-between items-center">
                <span className="text-lg font-medium text-gray-700">
                  Total Price:
                </span>
                <span className="text-2xl font-bold text-green-600">
                  ₦{(material.pricePerKg * quantity).toFixed(2)}
                </span>
              </div>
            </div>

            {/* Message */}
            {message && (
              <div
                className={`p-3 rounded-md ${
                  message.includes("successfully")
                    ? "bg-green-100 text-green-700 border border-green-200"
                    : "bg-red-100 text-red-700 border border-red-200"
                }`}
              >
                <div className="flex items-center">
                  {message.includes("successfully") ? (
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                  ) : (
                    <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                  )}
                  {message}
                </div>
              </div>
            )}

            {/* Add to Bin Button */}
            <button
              onClick={handleAddToBin}
              disabled={addingToBin || quantity <= 0}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-medium py-3 px-4 rounded-md transition-colors"
            >
              {addingToBin ? "Adding to Bin..." : "Add to Bin"}
            </button>
          </div>
        </div>
      </div>

      {/* Sample Images */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Sample Images
        </h2>

        {Array.isArray(material.sampleImages) && material.sampleImages.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {material.sampleImages.map((url, idx) => (
              <button
                type="button"
                key={`${url}-${idx}`}
                onClick={() => {
                  setActiveSampleUrl(url);
                  setSampleModalOpen(true);
                }}
                className="group relative overflow-hidden rounded-lg border bg-gray-50"
                title="View"
              >
                <img
                  src={url}
                  alt={`Sample ${idx + 1}`}
                  className="h-28 w-full object-cover transition-transform group-hover:scale-[1.02]"
                />
              </button>
            ))}
          </div>
        ) : (
          <div className="w-full rounded-lg border border-dashed p-8 text-center text-gray-600">
            No sample images available.
          </div>
        )}
      </div>

      {/* Sample image modal */}
      {sampleModalOpen && activeSampleUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => {
            setSampleModalOpen(false);
            setActiveSampleUrl(null);
          }}
        >
          <div
            className="relative w-full max-w-4xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => {
                setSampleModalOpen(false);
                setActiveSampleUrl(null);
              }}
              className="absolute -top-3 -right-3 rounded-full bg-white p-2 shadow"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5 text-gray-700" />
            </button>
            <img
              src={activeSampleUrl}
              alt="Sample"
              className="w-full max-h-[80vh] object-contain rounded-lg bg-white"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialDetails;
